import { Icon, IconName, IconProps } from '@ets/design-system';
import { CustomIcon } from '_common/lib/components';
import { getValidZipMime } from 'utils';

export type NodeIconProps = {
  disabled?: boolean;
  object: doDOC.BaseObject | doDOC.Space | Objekt;
  size?: IconProps['size'];
};

const FILE_VARIATIONS_EXTENSIONS: { extensions: string[]; icon: IconName }[] = [
  {
    extensions: ['.pdf'],
    icon: 'file-pdf',
  },
  {
    extensions: [
      '.ics',
      '.csv',
      '.323',
      '.uls',
      '.txt',
      '.rtx',
      '.sct',
      '.tsv',
      '.htt',
      '.htc',
      '.etx',
      '.vcf',
    ],
    icon: 'file-lines',
  },
  {
    extensions: [
      '.zip',
      '.rar',
      '.tar',
      '.sit',
      '.shar',
      '.gz',
      '.gtar',
      '.tgz',
      '.z',
      '.lzh',
      '.lha',
      '.arc',
      '.bz',
      '.bz2',
      '.jar',
      '.7z',
    ],
    icon: 'file-zipper',
  },
  {
    extensions: ['.latex', '.tex', '.texi', '.texinfo'],
    icon: 'file',
  },
  {
    extensions: [
      '.mp2',
      '.mpa',
      '.mpe',
      '.mpeg',
      '.mpg',
      '.mpv2',
      '.mp4',
      '.mov',
      '.qt',
      '.lsf',
      '.lsx',
      '.asf',
      '.asr',
      '.asx',
      '.avi',
      '.movie',
      '.ogg',
      '.ts',
      '.webm',
      '.3gp',
      '.3g2',
    ],
    icon: 'file-video',
  },
  {
    extensions: [
      '.avif',
      '.bmp',
      '.cod',
      '.gif',
      '.ief',
      '.jpe',
      '.jpeg',
      '.jpg',
      '.jfif',
      '.svg',
      '.tif',
      '.tiff',
      '.ras',
      '.cmx',
      '.ico',
      '.png',
      '.pnm',
      '.pbm',
      '.pgm',
      '.ppm',
      '.rgb',
      '.webp',
      '.xbm',
      '.xpm',
      '.xwd',
    ],
    icon: 'file-image',
  },
  {
    extensions: ['.css', '.htm', '.html', '.stm', '.xhtml'],
    icon: 'file-code',
  },
  {
    extensions: ['.eot', '.ttf', '.woff', '.woff2', '.etx', '.otf'],
    icon: 'file-signature',
  },
  {
    extensions: [
      '.j',
      '.java',
      '.js',
      '.py',
      '.pyc',
      '.php',
      '.c',
      '.cpp',
      '.cc',
      '.matlab',
      '.m',
      '.rb',
      '.SQL',
      '.bas',
      '.c',
      '.h',
    ],
    icon: 'file-code',
  },
  {
    extensions: ['.dwg', '.dxf', '.dwf', '.dgn', '.stl'],
    icon: 'file-cad',
  },
  {
    extensions: ['.doc', '.dot', '.word', '.odt', '.rtf', '.pages'],
    icon: 'file-lines',
  },
  {
    extensions: ['.xls', '.csv', '.numbers'],
    icon: 'file-csv',
  },
  {
    extensions: ['.ppt', '.keynote', '.pptx'],
    icon: 'file-ppt',
  },
];

const FILE_VARIATIONS_MIME_TYPE: { mimeTypes: string[]; icon: IconName }[] = [
  {
    mimeTypes: ['application/pdf'],
    icon: 'file-pdf',
  },
  {
    mimeTypes: [
      'text/calendar',
      'text/csv',
      'text/h323',
      'text/iuls',
      'text/richtext',
      'text/scriptlet',
      'text/tab-separated-values',
      'text/webviewhtml',
      'text/x-component',
      'text/x-setext',
      'text/x-vcard',
    ],
    icon: 'file-lines',
  },
  {
    mimeTypes: [
      'application/zip',
      'application/vnd.rar',
      'application/x-tar',
      'application/x-stuffit',
      'application/x-shar',
      'application/x-gzip',
      'application/x-gtar',
      'application/x-compressed',
      'application/x-compress',
      'application/x-freearc',
      'application/x-bzip',
      'application/x-bzip2',
      'application/java-archive',
      'application/x-7z-compressed',
    ],
    icon: 'file-zipper',
  },
  {
    mimeTypes: ['application/x-latex', 'application/x-tex', 'application/x-texinfo'],
    icon: 'file',
  },
  {
    mimeTypes: [
      'video/mpeg',
      'video/mp4',
      'video/quicktime',
      'video/x-la-asf',
      'video/x-ms-asf',
      'video/x-msvideo',
      'video/x-sgi-movie',
      'video/ogg',
      'video/mp2t',
      'video/webm',
      'video/3gpp',
      'video/3gpp2',
    ],
    icon: 'file-video',
  },
  {
    mimeTypes: [
      'image/avif',
      'image/bmp',
      'image/cis-cod',
      'image/gif',
      'image/ief',
      'image/jpeg',
      'image/pipeg',
      'image/svg+xml',
      'image/tiff',
      'image/x-cmu-raster',
      'image/x-cmx',
      'image/x-icon',
      'image/png',
      'image/x-portable-anymap',
      'image/x-portable-bitmap',
      'image/x-portable-graymap',
      'image/x-portable-pixmap',
      'image/x-rgb',
      'image/webp',
      'image/x-xbitmap',
      'image/x-xpixmap',
      'image/x-xwindowdump',
    ],
    icon: 'file-image',
  },
  {
    mimeTypes: ['text/css', 'text/html'],
    icon: 'file-code',
  },
  {
    mimeTypes: [
      'application/vnd.ms-fontobject',
      'font/ttf',
      'font/woff',
      'font/woff2',
      'text/x-setext',
      'font/otf',
      'application/font-sfnt',
    ],
    icon: 'file-signature',
  },
  {
    mimeTypes: [
      'text/x-java-source',
      'application/x-javascript',
      'text/x-script.phyton',
      'application/x-bytecode.python',
      'application/x-httpd-php',
      'text/x-c',
      'text/x-m',
    ],
    icon: 'file-code',
  },
  {
    mimeTypes: [
      'image/x-dwg',
      'image/x-dxf',
      'drawing/x-dwf',
      'application/dgn',
      'application/vnd.ms-pki.stl',
    ],
    icon: 'file-cad',
  },
  {
    mimeTypes: [
      'application/msword',
      'application/vnd.oasis.opendocument.text',
      'application/rtf',
      'application/vnd.apple.pages',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    icon: 'file-lines',
  },
  {
    mimeTypes: [
      'application/vnd.ms-excel',
      'text/csv',
      'application/vnd.apple.numbers',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    icon: 'file-excel',
  },
  {
    mimeTypes: [
      'vnd.ms-powerpoint',
      'application/vnd.apple.keynote',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    ],
    icon: 'file-ppt',
  },
];

const NodeIcon = ({ object, disabled, size = '3' }: NodeIconProps) => {
  // --------------------------------------
  if (object?.type === 'file' && object.wopi?.actions.length) {
    const app = object.wopi.actions[0].app;
    const sizeValue = size === '4' ? 32 : 16;
    const getIconName = () => {
      if (app === 'Excel') {
        return 'FileExcel';
      } else if (app === 'PowerPoint') {
        return 'FilePowerPoint';
      } else {
        return 'FileWord';
      }
    };

    return <CustomIcon name={getIconName()} size={sizeValue} />;
  }

  // --------------------------------------
  // ---------- icon selection ------------
  let nodeMimeType: string;

  if (object?.type === 'file' && object.mime) {
    //If application/zip, get another mime_type based on extension
    nodeMimeType = getValidZipMime(object) || object.mime.type;
  }

  const getIcon = (): IconName => {
    switch (object?.type) {
      case 'presentation':
        return 'file-chart-pie';
      case 'dopdf':
        return 'file-contract';
      case 'space':
        return 'box-archive';
      case 'group':
        return 'users';
      case 'folder':
        if (object.status === 'broken') {
          return 'folder-xmark';
        } else {
          return 'folder';
        }
      case 'document':
        if (object.status === 'broken') {
          return 'file-half-dashed';
        } else {
          return 'file-lines';
        }
      case 'file': {
        switch (nodeMimeType) {
          case 'application/octet-stream':
          case 'text/plain':
            return (
              FILE_VARIATIONS_EXTENSIONS.find((variation) =>
                variation.extensions.some((extension) => object.name.endsWith(extension)),
              )?.icon ?? 'file'
            );
          default:
            return (
              FILE_VARIATIONS_MIME_TYPE.find((variation) =>
                variation.mimeTypes.some((mimeType) => mimeType === nodeMimeType),
              )?.icon ?? 'file'
            );
        }
      }
      default:
        return 'file';
    }
  };

  const getBadge = () => {
    switch (object?.type) {
      case 'folder': {
        if (object.status === 'approved') {
          return {
            position: 'bottom-right',
            icon: 'circle-check',
            iconPrefix: 'fas',
            size: '1',
            color: 'success',
          };
        }
        break;
      }
      case 'document': {
        if (object.status === 'approved') {
          return {
            position: 'bottom-right',
            icon: 'circle-check',
            iconPrefix: 'fas',
            size: '1',
            color: 'success',
          };
        }
        break;
      }
      default:
        return null;
    }
  };

  const typeColorMap: Record<string, 'yellow' | 'error' | 'neutral' | 'primary'> = {
    presentation: 'yellow',
    dopdf: 'error',
    file: 'neutral',
  };

  const getColor = () => typeColorMap[object?.type ?? ''] || 'primary';

  return (
    <Icon
      size={size}
      icon={getIcon()}
      badge={getBadge()}
      disabled={disabled}
      testId="node-icon"
      color={getColor()}
    />
  );
};

export default NodeIcon;
